import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import DatePicker from 'react-datepicker'
import { ArrowLeft } from 'react-bootstrap-icons'
import { format } from 'date-fns'
import Modal from '../../../../../Modal/Modal'
import Input from '../../../../../FormComponents/Input'
import Textarea from '../../../../../FormComponents/Textarea'
import ProjectAssetPhaseSelect from '../../../../../FormComponents/ProjectAssetPhaseSelect'
import { toastError } from '../../../../../FormComponents/Toasts'

const parseDate = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export default observer(function EditAssetModal({
  isOpen,
  onClose,
  asset,
  initialStartDate,
  initialEndDate,
  initialNote,
  initialPhase,
  initialName,
  showPhaseSelect = false,
  showNameField = false,
  campaignStartDate,
  campaignEndDate,
  onSave,
}) {
  const [startDate, setStartDate] = useState(parseDate(initialStartDate))
  const [endDate, setEndDate] = useState(parseDate(initialEndDate))
  const [note, setNote] = useState(initialNote || '')
  const [phase, setPhase] = useState(initialPhase || 'main')
  const [name, setName] = useState(initialName || '')
  const [error, setError] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setStartDate(parseDate(initialStartDate))
    setEndDate(parseDate(initialEndDate))
    setNote(initialNote || '')
    setPhase(initialPhase || 'main')
    setName(initialName || '')
    setError('')
    setIsSaving(false)
  }, [isOpen, initialStartDate, initialEndDate, initialNote, initialPhase, initialName])

  const minStartDate = parseDate(campaignStartDate) || undefined
  const maxEndDate = parseDate(campaignEndDate) || undefined

  const handleSave = async () => {
    if (showNameField && !name.trim()) {
      setError('Name is required.')
      return
    }

    const hasStart = Boolean(startDate)
    const hasEnd = Boolean(endDate)

    if (hasStart !== hasEnd) {
      setError('Provide both dates, or leave both empty.')
      return
    }

    if (hasStart && hasEnd && endDate < startDate) {
      setError('End date must be on or after start date.')
      return
    }

    const formattedStart = hasStart ? format(startDate, 'yyyy-MM-dd') : ''
    const formattedEnd = hasEnd ? format(endDate, 'yyyy-MM-dd') : ''

    try {
      setIsSaving(true)
      await onSave({
        startDate: formattedStart,
        endDate: formattedEnd,
        note: note,
        phase: showPhaseSelect ? phase : undefined,
        name: showNameField ? name.trim() : undefined,
      })
      onClose()
    } catch (e) {
      const data = e?.data
      const message =
        (typeof data?.error === 'string' && data.error) ||
        (Array.isArray(data?.errors) && data.errors.filter(Boolean).join(', ')) ||
        'Failed to save changes. Please try again.'
      toastError(message)
      setError(message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} hasSideBar>
      <Modal.Body>
        <div className='p-2 dark:bg-rb-dark-mode w-[420px]'>
          <div className='flex justify-between items-center pb-4'>
            <h2 className='font-bold text-2xl dark:text-white'>Edit Asset</h2>
            <Modal.DissmissButton className='flex items-center p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white'>
              <ArrowLeft className='mr-1 font-bold' />
              <span className='font-bold'>Back</span>
            </Modal.DissmissButton>
          </div>

          {showNameField ? (
            <Input
              noLabelPadding
              label="Name"
              placeholder="Asset name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setError('')
              }}
              topMargin="mt-0"
            />
          ) : (
            asset?.name && (
              <p className='mb-4 text-sm text-rb-lighter-grey dark:text-gray-400 truncate'>
                {asset.auth_username ? `${asset.auth_username} – ` : ''}
                {asset.name}
              </p>
            )
          )}

          {showPhaseSelect && (
            <ProjectAssetPhaseSelect
              value={phase}
              onChange={(e) => setPhase(e.target.value)}
              topMargin="mt-4"
            />
          )}

          <div className='flex gap-4 mb-2 mt-4'>
            <div className='flex-1'>
              <label className='block mb-2 font-medium dark:text-white'>Start Date</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  setStartDate(date)
                  setError('')
                }}
                isClearable
                wrapperClassName='w-full'
                className='w-full p-2 border dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white rounded-lg'
                dateFormat='dd.MM.yyyy'
                showPopperArrow={false}
                popperClassName='react-datepicker-popper'
                popperPlacement='bottom-start'
                calendarClassName='react-datepicker'
                minDate={minStartDate}
                maxDate={maxEndDate}
              />
            </div>
            <div className='flex-1'>
              <label className='block mb-2 font-medium dark:text-white'>End Date</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  setEndDate(date)
                  setError('')
                }}
                isClearable
                wrapperClassName='w-full'
                className='w-full p-2 border dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white rounded-lg'
                dateFormat='dd.MM.yyyy'
                showPopperArrow={false}
                popperClassName='react-datepicker-popper'
                popperPlacement='bottom-start'
                calendarClassName='react-datepicker'
                minDate={startDate || minStartDate}
                maxDate={maxEndDate}
              />
            </div>
          </div>

          <Textarea
            label='Note'
            placeholder='Add a note about this asset (optional)'
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            topMargin='mt-4'
            noLabelPadding
          />

          {error && (
            <p className='text-red-500 text-sm mt-3'>{error}</p>
          )}

          <div className='flex justify-between mt-6'>
            <Modal.DissmissButton className='rb-btn-cancel font-bold text-lg p-2 px-4 rounded-lg'>
              <span className='font-bold'>Cancel</span>
            </Modal.DissmissButton>
            <button
              type='button'
              onClick={handleSave}
              disabled={isSaving}
              className='ml-auto bg-rb-green text-white font-bold rounded-lg p-2 px-4 submit text-center active:shadow-outer active:shadow-rb-green border-rb-green dark:text-rb-dark-mode disabled:opacity-60'
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
})
